/**
 * /for-carriers/start — "Become a client" carrier onboarding.
 *
 * Carriers can become clients of The Figure Co. This is the BASE conversion flow
 * (owner: "we can always customize later but we need base first"): a lead-capture
 * / onboarding-request form. On submit, platform staff follow up to provision the
 * carrier tenant (create the org + set `law_firms.client_side = "carrier"`); the
 * carrier's people then sign in with their normal roles and the firm setting flips
 * FRAMING only. No self-serve account creation yet (that's the "customize later").
 *
 * Reuses the proven waitlist intake endpoint + the court-exhibit auth shell so no
 * new backend surface is required for the base. Carrier leads are distinguished by
 * their carrier-side role value.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import AuthLayout from '../components/auth/AuthLayout';
import { AuthField, AuthSelect, AuthSubmit, AuthError } from '../components/auth/authForm';

export default function CarrierSignupPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');
    try {
      // Base: reuse the waitlist intake pipeline. `audience` marks carrier leads;
      // the endpoint ignores unknown fields, and the carrier-side role value is the
      // durable distinguisher if it doesn't.
      await api.post('/api/waitlist', { ...formData, audience: 'carrier' }, { showErrorToast: false });
      setStatus('success');
    } catch (error: any) {
      setErrorMessage(error?.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  if (status === 'success') {
    return (
      <AuthLayout title="We’ve got it." subtitle="Your carrier onboarding request is in." framed={false}>
        <p className="font-body text-sm leading-relaxed text-text-secondary">
          Our team will reach out to stand up your side — provision your workspace and walk your first
          matter. Expect to hear from us within one business day.
        </p>
        <div className="mt-6 flex flex-wrap gap-6">
          <Link
            to="/for-carriers"
            className="inline-flex font-mono text-xs uppercase tracking-[0.12em] text-text-primary underline decoration-[var(--color-border-strong)] underline-offset-4 transition-colors hover:text-brand-text hover:decoration-current"
          >
            Back to carriers
          </Link>
          <Link
            to="/"
            className="inline-flex font-mono text-xs uppercase tracking-[0.12em] text-text-secondary underline decoration-[var(--color-border-strong)] underline-offset-4 transition-colors hover:text-text-primary hover:decoration-current"
          >
            Homepage
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Become a client"
      subtitle="Start your carrier onboarding — we’ll stand up your side on a real matter."
      altPrompt="Already have an account?"
      altLinkText="Sign in"
      altLinkTo="/sign-in"
      framed={false}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AuthField id="firstName" name="firstName" label="First name" value={formData.firstName} onChange={handleChange} required placeholder="Jane" autoComplete="given-name" />
          <AuthField id="lastName" name="lastName" label="Last name" value={formData.lastName} onChange={handleChange} required placeholder="Roe" autoComplete="family-name" />
        </div>
        <AuthField id="email" name="email" label="Work email" type="email" value={formData.email} onChange={handleChange} required placeholder="jane@carrier.com" autoComplete="email" />
        <AuthField id="company" name="company" label="Carrier / organization" value={formData.company} onChange={handleChange} required placeholder="Your carrier or TPA" autoComplete="organization" />
        <AuthSelect id="role" name="role" label="Your role" value={formData.role} onChange={handleChange} required>
          <option value="">Select your role…</option>
          <option value="claims_leadership">Claims leadership</option>
          <option value="adjuster">Adjuster / examiner</option>
          <option value="defense_counsel">Defense counsel</option>
          <option value="siu">SIU / fraud</option>
          <option value="other">Other</option>
        </AuthSelect>

        {status === 'error' && <AuthError>{errorMessage}</AuthError>}

        <AuthSubmit loading={status === 'loading'} loadingText="Submitting…">
          Become a client
        </AuthSubmit>

        <p className="font-mono text-[11px] leading-relaxed text-text-dim">
          We onboard carrier firms directly — no self-serve signup yet. By submitting you agree to our{' '}
          <Link to="/privacy" className="underline decoration-[var(--color-border-strong)] underline-offset-2 hover:text-text-secondary hover:decoration-current">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link to="/terms" className="underline decoration-[var(--color-border-strong)] underline-offset-2 hover:text-text-secondary hover:decoration-current">
            Terms of Service
          </Link>.
        </p>
      </form>
    </AuthLayout>
  );
}
